import React, { useEffect, useState } from "react";
import { db } from "../firebase";

import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  runTransaction,
} from "firebase/firestore";

import "./AdminBetCorrectScore.css";

export default function AdminCorrectScoreRequest() {
  const [bets, setBets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchMatch, setSearchMatch] = useState("");
  const [message, setMessage] = useState("");
  const [processingId, setProcessingId] = useState(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const fieldMap = {
    TZS: "tshBalance",
    USDT: "usdtBalance",
    KES: "KESBalance",
    UGX: "UGXBalance",
    RWF: "RWFBalance",
    BIF: "BIFBalance",
    ZMW: "ZMWBalance",
    MWK: "MWKBalance",
    MZN: "MZNBalance",
    USD: "USDBalance",
    SSP: "SSPBalance",
    BWP: "BWPBalance",
    MGA: "MGABalance",
  };

  /* =========================================================
     LOAD BETS
     ========================================================= */

  useEffect(() => {
    fetchBets();
  }, []);

  async function fetchBets() {
    setLoading(true);

    try {
      const snap = await getDocs(
        collection(db, "correctscore")
      );

      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      setBets(list);
    } catch (error) {
      console.error("Fetch bets error:", error);
      showMessage("❌ Imeshindikana kupata bets");
    } finally {
      setLoading(false);
    }
  }

  /* =========================================================
     MESSAGE
     ========================================================= */

  const showMessage = (msg) => {
    setMessage(msg);

    setTimeout(() => {
      setMessage("");
    }, 3000);
  };

  /* =========================================================
     DATE FORMAT
     ========================================================= */

  const formatDateTime = (timestamp) => {
    if (!timestamp) return "-";

    try {
      const date = timestamp?.toDate
        ? timestamp.toDate()
        : new Date(timestamp);

      if (Number.isNaN(date.getTime())) {
        return "-";
      }

      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch (error) {
      console.error("Date format error:", error);
      return "-";
    }
  };

  /* =========================================================
     MATCH DATE
     ========================================================= */

  const getMatchDate = (bet) => {
    try {
      if (bet.matchTimestamp?.toDate) {
        return bet.matchTimestamp.toDate();
      }

      if (bet.matchTimestamp) {
        const parsed = new Date(bet.matchTimestamp);

        if (!Number.isNaN(parsed.getTime())) {
          return parsed;
        }
      }

      if (bet.matchDate && bet.matchTime) {
        const parsed = new Date(
          `${bet.matchDate}T${bet.matchTime}:00+03:00`
        );

        if (!Number.isNaN(parsed.getTime())) {
          return parsed;
        }
      }

      if (bet.matchDate) {
        const parsed = new Date(
          `${bet.matchDate}T00:00:00+03:00`
        );

        if (!Number.isNaN(parsed.getTime())) {
          return parsed;
        }
      }

      return null;
    } catch (error) {
      console.error("Match date error:", error);
      return null;
    }
  };

  /* =========================================================
     CHECK IF MATCH HAS STARTED / PASSED
     ========================================================= */

  const isPastMatch = (bet) => {
    const matchDate = getMatchDate(bet);

    if (!matchDate) {
      return false;
    }

    return matchDate.getTime() <= Date.now();
  };

  /* =========================================================
     WON SINGLE
     ========================================================= */

  async function handleWonBet(bet) {
    if (processingId === bet.id) {
      return;
    }

    if (bet.paidOut === true) {
      showMessage("⚠️ Tayari imelipwa");
      return;
    }

    if (bet.result) {
      showMessage("⚠️ Bet tayari imefungwa");
      return;
    }

    if (!isPastMatch(bet)) {
      showMessage("⚠️ Mechi bado haijaanza/haijaisha");
      return;
    }

    const field = fieldMap[bet.currency];

    if (!field) {
      showMessage("❌ Currency haijulikani");
      return;
    }

    const payout = Number(bet.totalWin || 0);

    if (!Number.isFinite(payout) || payout <= 0) {
      showMessage("❌ Payout si sahihi");
      return;
    }

    setProcessingId(bet.id);

    try {
      const q = query(
        collection(db, "users"),
        where("username", "==", bet.username)
      );

      const snap = await getDocs(q);

      if (snap.empty) {
        showMessage("❌ User not found");
        return;
      }

      const userDoc = snap.docs[0];

      const userRef = doc(
        db,
        "users",
        userDoc.id
      );

      const betRef = doc(
        db,
        "correctscore",
        bet.id
      );

      await runTransaction(
        db,
        async (transaction) => {
          const betSnap =
            await transaction.get(betRef);

          if (!betSnap.exists()) {
            throw new Error("Bet not found");
          }

          const freshBet = betSnap.data();

          if (freshBet.result) {
            throw new Error(
              "Bet tayari imefungwa"
            );
          }

          if (freshBet.paidOut === true) {
            throw new Error(
              "Bet tayari imelipwa"
            );
          }

          const userSnap =
            await transaction.get(userRef);

          if (!userSnap.exists()) {
            throw new Error(
              "User not found"
            );
          }

          const userData =
            userSnap.data();

          const currentBalance =
            Number(userData[field] || 0);

          const freshPayout =
            Number(freshBet.totalWin || 0);

          if (
            !Number.isFinite(freshPayout) ||
            freshPayout <= 0
          ) {
            throw new Error(
              "Invalid payout"
            );
          }

          transaction.update(
            userRef,
            {
              [field]:
                currentBalance +
                freshPayout,
            }
          );

          transaction.update(
            betRef,
            {
              result: "win",
              paidOut: true,
              paidAt: new Date(),
            }
          );
        }
      );

      showMessage(
        `✅ Bet ${bet.betId || ""} imelipwa`
      );

      await fetchBets();
    } catch (err) {
      console.error(
        "Won bet error:",
        err
      );

      showMessage(
        `❌ ${err.message || "Error"}`
      );
    } finally {
      setProcessingId(null);
    }
  }

  /* =========================================================
     LOST SINGLE
     ========================================================= */

  async function handleLost(bet) {
    if (processingId === bet.id) {
      return;
    }

    if (bet.result) {
      showMessage("⚠️ Bet tayari imefungwa");
      return;
    }

    if (!isPastMatch(bet)) {
      showMessage("⚠️ Mechi bado haijaanza/haijaisha");
      return;
    }

    setProcessingId(bet.id);

    try {
      await updateDoc(
        doc(db, "correctscore", bet.id),
        {
          result: "lose",
          lostAt: new Date(),
        }
      );

      showMessage(
        `❌ Bet ${bet.betId || ""} imewekwa Lost`
      );

      await fetchBets();
    } catch (error) {
      console.error(
        "Lost bet error:",
        error
      );

      showMessage(
        "❌ Imeshindikana kuweka Lost"
      );
    } finally {
      setProcessingId(null);
    }
  }

  /* =========================================================
     DELETE BET
     ========================================================= */

  async function deleteBet(bet) {
    if (processingId === bet.id) {
      return;
    }

    const confirmed =
      window.confirm(
        `Una uhakika unataka kufuta bet ${
          bet.betId || bet.id
        }?`
      );

    if (!confirmed) {
      return;
    }

    setProcessingId(bet.id);

    try {
      await deleteDoc(
        doc(
          db,
          "correctscore",
          bet.id
        )
      );

      showMessage("🗑️ Bet imefutwa");

      await fetchBets();
    } catch (error) {
      console.error(
        "Delete bet error:",
        error
      );

      showMessage(
        "❌ Imeshindikana kufuta bet"
      );
    } finally {
      setProcessingId(null);
    }
  }

  /* =========================================================
     ALL WON
     ========================================================= */

  async function handleAllWon() {
    if (bulkProcessing) {
      return;
    }

    const eligibleBets = bets.filter(
      (bet) =>
        !bet.result &&
        bet.paidOut !== true &&
        isPastMatch(bet)
    );

    if (eligibleBets.length === 0) {
      showMessage(
        "⚠️ Hakuna bets zinazoweza kulipwa"
      );
      return;
    }

    const confirmed =
      window.confirm(
        `Unataka kulipa bets ${eligibleBets.length} zote?`
      );

    if (!confirmed) {
      return;
    }

    setBulkProcessing(true);

    let success = 0;
    let failed = 0;

    try {
      for (const bet of eligibleBets) {
        try {
          await payBetWithoutReload(bet);
          success++;
        } catch (error) {
          console.error(
            "Bulk won error:",
            error
          );
          failed++;
        }
      }

      await fetchBets();

      if (failed === 0) {
        showMessage(
          `✅ Bets ${success} zote zimelipwa`
        );
      } else {
        showMessage(
          `⚠️ Zilizolipwa: ${success}, zilizoshindikana: ${failed}`
        );
      }
    } finally {
      setBulkProcessing(false);
    }
  }

  /* =========================================================
     PAY BET WITHOUT RELOAD
     ========================================================= */

  async function payBetWithoutReload(bet) {
    const field = fieldMap[bet.currency];

    if (!field) {
      throw new Error(
        "Currency haijulikani"
      );
    }

    const q = query(
      collection(db, "users"),
      where("username", "==", bet.username)
    );

    const snap = await getDocs(q);

    if (snap.empty) {
      throw new Error(
        "User not found"
      );
    }

    const userDoc = snap.docs[0];

    const userRef = doc(
      db,
      "users",
      userDoc.id
    );

    const betRef = doc(
      db,
      "correctscore",
      bet.id
    );

    await runTransaction(
      db,
      async (transaction) => {
        const betSnap =
          await transaction.get(betRef);

        if (!betSnap.exists()) {
          throw new Error(
            "Bet not found"
          );
        }

        const freshBet =
          betSnap.data();

        if (freshBet.result) {
          throw new Error(
            "Bet tayari imefungwa"
          );
        }

        if (freshBet.paidOut === true) {
          throw new Error(
            "Bet tayari imelipwa"
          );
        }

        const userSnap =
          await transaction.get(userRef);

        if (!userSnap.exists()) {
          throw new Error(
            "User not found"
          );
        }

        const userData =
          userSnap.data();

        const currentBalance =
          Number(userData[field] || 0);

        const payout =
          Number(
            freshBet.totalWin || 0
          );

        if (
          !Number.isFinite(payout) ||
          payout <= 0
        ) {
          throw new Error(
            "Invalid payout"
          );
        }

        transaction.update(
          userRef,
          {
            [field]:
              currentBalance +
              payout,
          }
        );

        transaction.update(
          betRef,
          {
            result: "win",
            paidOut: true,
            paidAt: new Date(),
          }
        );
      }
    );
  }

  /* =========================================================
     ALL LOST
     ========================================================= */

  async function handleAllLost() {
    if (bulkProcessing) {
      return;
    }

    const eligibleBets = bets.filter(
      (bet) =>
        !bet.result &&
        isPastMatch(bet)
    );

    if (eligibleBets.length === 0) {
      showMessage(
        "⚠️ Hakuna bets zinazoweza kuwekwa Lost"
      );
      return;
    }

    const confirmed =
      window.confirm(
        `Unataka kuweka Lost bets ${eligibleBets.length} zote?`
      );

    if (!confirmed) {
      return;
    }

    setBulkProcessing(true);

    let success = 0;
    let failed = 0;

    try {
      for (const bet of eligibleBets) {
        try {
          await updateDoc(
            doc(
              db,
              "correctscore",
              bet.id
            ),
            {
              result: "lose",
              lostAt: new Date(),
            }
          );

          success++;
        } catch (error) {
          console.error(
            "Bulk lost error:",
            error
          );

          failed++;
        }
      }

      await fetchBets();

      if (failed === 0) {
        showMessage(
          `❌ Bets ${success} zote zimewekwa Lost`
        );
      } else {
        showMessage(
          `⚠️ Lost: ${success}, Failed: ${failed}`
        );
      }
    } finally {
      setBulkProcessing(false);
    }
  }

  /* =========================================================
     SEARCH + PENDING
     ========================================================= */

  const pending = bets
    .filter((bet) => {
      const search =
        searchMatch
          .trim()
          .toLowerCase();

      if (!search) {
        return true;
      }

      return `${bet.teamA || ""} ${
        bet.teamB || ""
      } ${bet.username || ""} ${
        bet.betId || ""
      } ${bet.league || ""}`
        .toLowerCase()
        .includes(search);
    })
    .filter(
      (bet) => !bet.result
    );

  /* =========================================================
     TOTAL PENDING
     ========================================================= */

  const totalPending =
    pending.length;

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div className="admin-ant-container">

      {/* =====================================================
          TITLE
          ===================================================== */}

      <h2>
        Admin Bets (CorrectScore Only)
      </h2>

      {/* =====================================================
          SEARCH
          ===================================================== */}

      <input
        type="text"
        placeholder="Search match / user / Bet ID"
        value={searchMatch}
        onChange={(e) =>
          setSearchMatch(
            e.target.value
          )
        }
      />

      {/* =====================================================
          MESSAGE
          ===================================================== */}

      {message && (
        <p>
          {message}
        </p>
      )}

      {/* =====================================================
          LOADING
          ===================================================== */}

      {loading ? (
        <p className="admin-loading">
          Loading...
        </p>
      ) : (
        <>
          {/* ================================================
              SUMMARY
              ================================================ */}

          <div className="summary-card">
            <h3>
              Pending CorrectScore Bets
            </h3>

            <p>
              {totalPending}
            </p>
          </div>

          {/* ================================================
              TABLE WRAPPER
              ================================================ */}

          <div className="table-wrapper">

            <table>

              <thead>
                <tr>
                  <th>User</th>
                  <th>Bet ID</th>
                  <th>Type</th>
                  <th>Match</th>
                  <th>League</th>
                  <th>Match Date</th>
                  <th>Match Time</th>
                  <th>Match Timestamp</th>
                  <th>Bet Time</th>
                  <th>Score</th>
                  <th>Odds</th>
                  <th>Amount</th>
                  <th>TotalWin</th>
                  <th>Currency</th>
                  <th>Status</th>
                  <th>Paid</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>

                {pending.length === 0 ? (
                  <tr>
                    <td colSpan="17">
                      No pending bets
                    </td>
                  </tr>
                ) : (
                  pending.map(
                    (bet) => {

                      const processing =
                        processingId ===
                        bet.id;

                      return (
                        <tr
                          key={bet.id}
                        >

                          {/* USER */}

                          <td>
                            {bet.username ||
                              "-"}
                          </td>

                          {/* BET ID */}

                          <td>
                            {bet.betId ||
                              bet.id ||
                              "-"}
                          </td>

                          {/* TYPE */}

                          <td>
                            {bet.type ||
                              "Correct Score"}
                          </td>

                          {/* MATCH */}

                          <td>
                            {bet.teamA ||
                              "-"}{" "}
                            vs{" "}
                            {bet.teamB ||
                              "-"}
                          </td>

                          {/* LEAGUE */}

                          <td>
                            {bet.league ||
                              "-"}
                          </td>

                          {/* MATCH DATE */}

                          <td>
                            {bet.matchDate ||
                              "-"}
                          </td>

                          {/* MATCH TIME */}

                          <td>
                            {bet.matchTime ||
                              "-"}
                          </td>

                          {/* MATCH TIMESTAMP */}

                          <td>
                            {formatDateTime(
                              bet.matchTimestamp
                            )}
                          </td>

                          {/* BET TIME */}

                          <td>
                            {formatDateTime(
                              bet.createdAt
                            )}
                          </td>

                          {/* SCORE */}

                          <td>
                            {bet.score ||
                              "-"}
                          </td>

                          {/* ODDS */}

                          <td>
                            {bet.odds ||
                              "-"}
                          </td>

                          {/* AMOUNT */}

                          <td>
                            {Number(
                              bet.amount || 0
                            ).toLocaleString()}
                          </td>

                          {/* TOTAL WIN */}

                          <td>
                            {Number(
                              bet.totalWin ||
                                0
                            ).toLocaleString()}
                          </td>

                          {/* CURRENCY */}

                          <td>
                            {bet.currency ||
                              "-"}
                          </td>

                          {/* STATUS */}

                          <td>
                            PENDING
                          </td>

                          {/* PAID */}

                          <td>
                            ❌
                          </td>

                          {/* ACTIONS */}

                          <td>

                            <button
                              onClick={() =>
                                handleWonBet(
                                  bet
                                )
                              }
                              disabled={
                                processing ||
                                bulkProcessing
                              }
                            >
                              {processing
                                ? "..."
                                : "Won"}
                            </button>

                            <button
                              onClick={() =>
                                handleLost(
                                  bet
                                )
                              }
                              disabled={
                                processing ||
                                bulkProcessing
                              }
                            >
                              {processing
                                ? "..."
                                : "Lost"}
                            </button>

                            <button
                              onClick={() =>
                                deleteBet(
                                  bet
                                )
                              }
                              disabled={
                                processing ||
                                bulkProcessing
                              }
                            >
                              {processing
                                ? "..."
                                : "Delete"}
                            </button>

                          </td>

                        </tr>
                      );
                    }
                  )
                )}

              </tbody>

            </table>

          </div>

          {/* =================================================
              BULK ACTIONS
              ================================================= */}

          <div className="action-buttons">

            <button
              className="all-won-btn"
              onClick={handleAllWon}
              disabled={
                bulkProcessing
              }
            >
              {bulkProcessing
                ? "Processing..."
                : "🟢 All Won (Pay All)"}
            </button>

            <button
              className="all-lost-btn"
              onClick={handleAllLost}
              disabled={
                bulkProcessing
              }
            >
              {bulkProcessing
                ? "Processing..."
                : "🔴 All Lost"}
            </button>

          </div>

        </>
      )}

    </div>
  );
}